import type { ReactNode } from 'react';
import { workspaceBrandName } from '../branding/platform-brand.ts';
import { readAuthenticatedShellReloadPreviewSessionCache } from './authenticated-shell-reload-preview-session-cache.ts';
import OpenWorkTabAreaMark from './OpenWorkTabAreaMark.tsx';
import NavigationMenuCaret from './NavigationMenuCaret.tsx';
import ProfileAvatar from './ProfileAvatar.tsx';
import './layout.css';
import './authenticated-shell-reload-preview.css';

export default function AuthenticatedShellReloadPreview({
  children,
  primaryNavigationHidden,
  userId,
}: {
  children: ReactNode;
  primaryNavigationHidden: boolean;
  userId: string;
}) {
  const snapshot = readAuthenticatedShellReloadPreviewSessionCache({ userId });
  if (!snapshot) return null;

  return (
    <div className="app-layout authenticated-shell-reload-preview">
      {!primaryNavigationHidden && (
        <aside className="app-sidebar app-sidebar--light">
          <div className="app-sidebar-brand">
            <span className="app-firm-brand-name">{workspaceBrandName(snapshot.firmName)}</span>
          </div>
          <nav className="app-sidebar-navigation">
            <ul className="app-sidebar-list">
              {snapshot.sidebarGroups.map((group) => (
                <li className="app-sidebar-group" key={group.key}>
                  <div className="app-sidebar-section-toggle">
                    <span>{group.label}</span>
                    <span className="app-sidebar-section-toggle-icon">›</span>
                  </div>
                  {!group.collapsed && (
                    <ul className="app-sidebar-list app-sidebar-group-items">
                      {group.items.map((item) => (
                        <li
                          className={`app-sidebar-item${
                            location.pathname === item.key ||
                            location.pathname.startsWith(`${item.key}/`)
                              ? ' app-sidebar-item--selected'
                              : ''
                          }`}
                          key={item.key}
                        >
                          <span className="app-sidebar-item-icon" />
                          <span className="app-sidebar-item-label">{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      )}
      <div className="app-main-layout">
        <header className="app-topbar">
          {primaryNavigationHidden && (
            <div className="app-topbar-brand">
              <span className="app-firm-brand-name">{workspaceBrandName(snapshot.firmName)}</span>
            </div>
          )}
          <span className="app-topbar-icon-button authenticated-shell-reload-preview__nav-toggle">
            ◫
          </span>
          <nav className="app-open-work-tabs">
            {snapshot.tabs.map((tab) => (
              <div
                className={`app-open-work-tab${
                  tab.hasMenu || tab.areaMark ? ' app-open-work-tab--has-leading-slot' : ''
                }${tab.active ? ' app-open-work-tab--active' : ''}`}
                key={tab.id}
              >
                {tab.hasMenu ? (
                  <span className="app-open-work-tab-menu-button">
                    {tab.areaMark ? (
                      <OpenWorkTabAreaMark hasMenu mark={tab.areaMark} />
                    ) : (
                      <span className="app-open-work-tab-menu-indicator">
                        <NavigationMenuCaret className="app-open-work-tab-menu-caret" />
                      </span>
                    )}
                  </span>
                ) : (
                  tab.areaMark && (
                    <span className="app-open-work-tab-area-slot">
                      <OpenWorkTabAreaMark mark={tab.areaMark} />
                    </span>
                  )
                )}
                <span className="app-open-work-tab-link">
                  <span className="app-open-work-tab-title">{tab.title}</span>
                  {tab.section && <span className="app-open-work-tab-section">{tab.section}</span>}
                </span>
              </div>
            ))}
          </nav>
          <div className="app-topbar-actions">
            {Array.from({ length: snapshot.actionCount }, (_, index) => (
              <span className="app-topbar-action-placeholder" key={index} />
            ))}
            <div className="app-account-menu-owner">
              <span className="app-topbar-profile-trigger">
                <ProfileAvatar
                  src={snapshot.profile.avatarUrl}
                  email={snapshot.profile.email}
                  name={snapshot.profile.fullName}
                />
              </span>
              {snapshot.avatarBadgeCount > 0 && (
                <span className="app-account-badge" aria-hidden="true">
                  {Array.from({ length: snapshot.avatarBadgeCount }, (_, index) => (
                    <span className="app-topbar-avatar-action-slot" key={index} />
                  ))}
                </span>
              )}
            </div>
          </div>
        </header>
        <main className="app-content authenticated-shell-reload-preview__content">
          <div>{children}</div>
        </main>
      </div>
    </div>
  );
}
